import { v4 as uuidv4 } from "uuid";
import { xml2js, js2xml } from "xml-js";

// Define the session attachment metadata structure
export interface SessionAttachment {
  sessionId: string;
  sessionName: string;
  attachmentDate: string;
  workbookName?: string;
  sheetId: string;
  sheetName: string;
}

// XML namespace for Midship attachments
export const MIDSHIP_NAMESPACE = "http://schemas.midship.com/excel/attachment";

/**
 * Converts session data to XML format for storage in Excel
 */
export const sessionToXml = (sessionData: SessionAttachment): string => {
  // Create a properly structured XML object
  const xmlObj = {
    declaration: { attributes: { version: "1.0", encoding: "utf-8" } },
    elements: [
      {
        type: "element",
        name: "midship:attachment",
        attributes: {
          "xmlns:midship": MIDSHIP_NAMESPACE,
        },
        elements: [
          {
            type: "element",
            name: "midship:metadata",
            elements: [
              {
                type: "element",
                name: "midship:id",
                elements: [{ type: "text", text: uuidv4() }],
              },
              {
                type: "element",
                name: "midship:createdAt",
                elements: [{ type: "text", text: new Date().toISOString() }],
              },
              {
                type: "element",
                name: "midship:type",
                elements: [{ type: "text", text: "sheet_attachment" }],
              },
            ],
          },
          {
            type: "element",
            name: "midship:session",
            elements: [
              {
                type: "element",
                name: "midship:id",
                elements: [{ type: "text", text: sessionData.sessionId }],
              },
              {
                type: "element",
                name: "midship:name",
                elements: [{ type: "text", text: sessionData.sessionName }],
              },
              {
                type: "element",
                name: "midship:attachmentDate",
                elements: [{ type: "text", text: sessionData.attachmentDate || new Date().toISOString() }],
              },
            ],
          },
          {
            type: "element",
            name: "midship:sheet",
            elements: [
              {
                type: "element",
                name: "midship:id",
                elements: [{ type: "text", text: sessionData.sheetId }],
              },
              {
                type: "element",
                name: "midship:name",
                elements: [{ type: "text", text: sessionData.sheetName }],
              },
            ],
          },
          {
            type: "element",
            name: "midship:workbook",
            elements: [
              {
                type: "element",
                name: "midship:name",
                elements: [{ type: "text", text: sessionData.workbookName || "Untitled" }],
              },
            ],
          },
        ],
      },
    ],
  };

  // Convert to XML string
  return js2xml(xmlObj, { compact: false, spaces: 2 });
};

/**
 * Converts XML from Excel to session data
 */
export const xmlToSession = (xmlString: string): SessionAttachment | null => {
  try {
    // Parse XML to JS object
    const result = xml2js(xmlString, { compact: true });

    // Extract session data from the parsed XML
    const attachment = result["midship:attachment"];
    if (!attachment || !attachment["midship:session"] || !attachment["midship:sheet"]) {
      return null;
    }

    const session = attachment["midship:session"];
    const sheet = attachment["midship:sheet"];
    const workbook = attachment["midship:workbook"];

    return {
      sessionId: session["midship:id"]._text,
      sessionName: session["midship:name"]._text,
      attachmentDate: session["midship:attachmentDate"]._text,
      sheetId: sheet["midship:id"]._text,
      sheetName: sheet["midship:name"]._text,
      workbookName: workbook?.["midship:name"]?._text,
    };
  } catch (error) {
    return null;
  }
};

/**
 * Attaches the current sheet to a Midship session
 */
export const attachSheetToSession = async (sessionId: string, sessionName: string): Promise<boolean> => {
  try {
    let success = false;

    await Excel.run(async (context) => {
      // Get workbook and active sheet properties
      const workbookProperties = context.workbook.properties;
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();

      // Load required properties
      workbookProperties.load("title");
      activeSheet.load(["id", "name"]);
      await context.sync();

      // Create session attachment data
      const sessionData: SessionAttachment = {
        sessionId,
        sessionName,
        attachmentDate: new Date().toISOString(),
        workbookName: workbookProperties.title,
        sheetId: activeSheet.id,
        sheetName: activeSheet.name,
      };

      // Convert to XML
      const xmlString = sessionToXml(sessionData);

      // Store in customXmlParts
      const customXmlPart = context.workbook.customXmlParts.add(xmlString);
      customXmlPart.load("id");

      await context.sync();
      success = true;
    });

    return success;
  } catch (error) {
    return false;
  }
};

/**
 * Detaches the current sheet from any Midship session
 */
export const detachSheetFromSession = async (): Promise<boolean> => {
  try {
    let success = false;

    await Excel.run(async (context) => {
      // Get active sheet
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("id");
      await context.sync();

      const activeSheetId = activeSheet.id;

      // Get all customXmlParts with our namespace
      const customXmlParts = context.workbook.customXmlParts.getByNamespace(MIDSHIP_NAMESPACE);
      customXmlParts.load("items");
      await context.sync();

      // Look through XML parts for those matching the active sheet
      for (let i = 0; i < customXmlParts.items.length; i++) {
        const xmlBlob = customXmlParts.items[i].getXml();
        await context.sync();

        // Parse XML to check if it's for the current sheet
        const attachment = xmlToSession(xmlBlob.value);
        if (attachment && attachment.sheetId === activeSheetId) {
          // This XML part belongs to the current sheet, delete it
          customXmlParts.items[i].delete();
          success = true;
        }
      }

      await context.sync();

      activeSheet.load("name");
      await context.sync();
    });

    return success;
  } catch (error) {
    return false;
  }
};

/**
 * Checks if the current sheet is attached to a session
 */
export const getCurrentSheetSessionAttachment = async (): Promise<SessionAttachment | null> => {
  try {
    let sessionInfo: SessionAttachment | null = null;

    await Excel.run(async (context) => {
      // Get active sheet
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("id");
      await context.sync();

      const activeSheetId = activeSheet.id;

      // Get all customXmlParts with our namespace
      const customXmlParts = context.workbook.customXmlParts.getByNamespace(MIDSHIP_NAMESPACE);
      customXmlParts.load("items");
      await context.sync();

      // Look for attachment XML parts matching the current sheet
      for (let i = 0; i < customXmlParts.items.length; i++) {
        const xmlBlob = customXmlParts.items[i].getXml();
        await context.sync();

        // Parse the XML to get session info
        const attachment = xmlToSession(xmlBlob.value);
        if (attachment && attachment.sheetId === activeSheetId) {
          sessionInfo = attachment;
          break;
        }
      }
    });

    return sessionInfo;
  } catch (error) {
    return null;
  }
};

// Keep legacy function names as aliases for backward compatibility
export const attachWorkbookToSession = attachSheetToSession;
export const detachWorkbookFromSession = detachSheetFromSession;
export const getWorkbookSessionAttachment = getCurrentSheetSessionAttachment;
